import { useMemo } from 'react';
import type {
  ExportAssetRange,
  ExportSettings,
  ExportSettingsInput,
} from '@frameio/graphql-client/generated';
import {
  useExportSettingsManager,
  type UseExportSettingsManagerReturnType,
} from '../../shared/hooks/useExportSettingsManager';
import { useExportSettingsValidation } from '../../shared/hooks/useExportSettingsValidation';
import { normalizeRootFolderName } from '../../shared/utils/normalizeRootFolderName';
import { useUpdateExportSettingsMutation } from '../gql/mutations/generated/UpdateExportSettings';
import { useGetExportSettingsQuery } from '../gql/queries/generated/GetExportSettings';
import { useLastExportAssetData } from './useLastExportAssetData';

export type UseSequenceExportSettingsReturnType =
  UseExportSettingsManagerReturnType<ExportSettings> & {
    assetName?: string;
    range: ExportAssetRange;
    settingsError: string | undefined;
  };

const DEFAULT_SETTINGS: ExportSettings = {
  localCopyEnabled: false,
  localFolderPath: null,
  assetFolderId: null,
  assetFolderName: null,
  presetId: null,
  range: 'entireSequence',
  exportMarkersAsCommentsEnabled: false,
};

const GQL_UXP_CONTEXT = {
  context: {
    resolveWithUXP: true,
  },
};

export const useSequenceExportSettings = ({
  isVersionStackMode = false,
  overrideFolderId,
}: {
  isVersionStackMode?: boolean;
  overrideFolderId?: string;
}): UseSequenceExportSettingsReturnType => {
  const { data: settingsData, loading } =
    useGetExportSettingsQuery(GQL_UXP_CONTEXT);

  const [storeSettingsMutation] =
    useUpdateExportSettingsMutation(GQL_UXP_CONTEXT);

  const storeMutation = async (
    input: ExportSettingsInput
  ): Promise<ExportSettings | null> => {
    const result = await storeSettingsMutation({
      variables: { input },
      refetchQueries: ['GetExportSettings'],
    });
    return result.data?.updateExportSettings ?? null;
  };

  const base = useExportSettingsManager<ExportSettings>({
    defaultSettings: DEFAULT_SETTINGS,
    uxpStorage: settingsData?.getExportSettings ?? null,
    loading,
    storeMutation,
    overrideFolderId,
  });

  const lastExportAssetData = useLastExportAssetData();
  const lastExportLocation = useMemo(
    () => getLastExportLocation(lastExportAssetData),
    [lastExportAssetData]
  );
  const assetName = useMemo(() => {
    return lastExportAssetData?.name ?? '';
  }, [lastExportAssetData]);

  const settingsError = useExportSettingsValidation({
    assetFolderId: isVersionStackMode
      ? lastExportLocation.assetFolderId
      : base.assetFolderId,
    localCopyEnabled: base.localCopyEnabled,
    localFolderPath: base.localFolderPath,
  });

  return {
    ...base,
    range: base.range ?? 'entireSequence',
    ...(isVersionStackMode ? lastExportLocation : {}),
    ...(isVersionStackMode ? { assetName } : {}),
    settingsError,
  };
};

const getLastExportLocation = (
  asset: ReturnType<typeof useLastExportAssetData>
) => {
  if (!asset) {
    return { assetFolderName: '', assetFolderId: '' };
  }

  let parent = asset.parent;
  if (parent?.__typename === 'VersionStackAsset') {
    parent = parent.parent;
  }

  const folderId = parent?.id ?? '';

  if (!folderId) {
    return { assetFolderName: '', assetFolderId: '' };
  }

  const folder = asset.ancestorFolders?.find((f) => f?.id === folderId);
  const folderName = folder?.name;

  return {
    assetFolderName:
      normalizeRootFolderName(folderName, asset.project?.name) ?? '',
    assetFolderId: folderId,
  };
};
